import Expand from "@arcgis/core/widgets/Expand.js";
import * as query from "@arcgis/core/rest/query.js";

import { setupParcelLink } from "./initializeSummaries";

let WinGISMap;
let conditionalMapOptions;

const pointSymbol = {
  type: "simple-marker", // autocasts as new SimpleMarkerSymbol()
  style: "square",
  color: [123, 123, 236, 0.15],
  size: "20px",
  outline: {
    color: "white",
    width: 5
  }
};

const symbol = {
  type: "simple-fill",  // autocasts as new SimpleFillSymbol()
  color: [123, 123, 236, 0.15],
  style: "solid",
  outline: {
    color: "white",
    width: 2
  }
};

export default async function (winGISMap, ConditionalMapOptions) {
  WinGISMap = winGISMap;
  conditionalMapOptions = ConditionalMapOptions;
  let searchBox; 
  let btnAdSearch;
  let clearBtn;
  if (conditionalMapOptions.addAdvancedUI) {
    // Advanced UI branch...
  } else {
    [searchBox, btnAdSearch, clearBtn] = await addPropertySearchBox(WinGISMap.view);
  }
  addSearchListeners(WinGISMap.view, conditionalMapOptions, searchBox, btnAdSearch, clearBtn);
}
 
function addPropertySearchBox(view) {
  return new Promise((resolve, reject) => {
    let propertySearchDiv = document.createElement('div');
    propertySearchDiv.classList.add("propertySearchDiv");
    propertySearchDiv.innerHTML = `
      <header class="header-title">Property Search</header> 
      <div class="esri-widget customWidget" id="search" role="tabpanel" aria-labelledby="search-tab">
        <div id="AdSearchDiv">
            <i>For addresses, omit the street direction and suffix. For example, '127 Wyman'.</i>
            <calcite-input-text class="mb-2" id="txtAdSearchString"></calcite-input-text>
            <calcite-button id="btnAdSearch" value="Search">Search</calcite-button>
            <calcite-button id="clrResults">Clear Results</calcite-button>
        </div>
        <div id="AdSearchResults"></div>
      </div>`;
    
    let propertySearchExpand = new Expand({
      view: view,
      expandIcon: "search",
      content: propertySearchDiv,
      expanded: true,
    });
    view.ui.add({
      component: propertySearchExpand,
      position: "bottom-left",
      index: 0
    });
    let searchBox = propertySearchDiv.querySelector("#txtAdSearchString");
    let btnAdSearch = propertySearchDiv.querySelector("#btnAdSearch");
    let clearBtn = propertySearchDiv.querySelector("#clrResults");
    resolve([searchBox, btnAdSearch, clearBtn]);
  });
}

function addSearchListeners(view, conditionalMapOptions, textBox, btnAdSearch, clearBtn) {
  // Clear button functionality
  if (!clearBtn) {
    clearBtn = document.getElementById("clrResults");
    if (!clearBtn) {
      clearBtn = document.getElementById("btnClearResults");
    }
  }

  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      document.getElementById("AdSearchResults").innerHTML = "";
      view.graphics = null;
    });
  }

  // Setup property search functionality
  let propertySearch;
  let setupPropertySearch = async function() {
    try {
      const propertySearchModule = await import(`../propertySearches/${conditionalMapOptions.propertySearch}.js`);
      propertySearch = propertySearchModule.default;
    } catch (error) {
      console.error("Error importing propertySearch module:", error);
    }
  };

  // Initialize the property search setup
  setupPropertySearch();

  /* URL PIN SEARCH */
  let rtsPin = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams(
    Array.from(rtsPin, ([key, value]) => [key.toLowerCase(), value])
  );
  
  WinGISMap.view.when(async () => {


    if (!textBox) {
      textBox = document.getElementById("txtAdSearchString");
    }

    if (newParams.has("pin")) {
      textBox.value = newParams.get("pin");
      setTimeout(() => {
        document.getElementById("btnAdSearch").click();
      }, 750);
    }

    textBox.addEventListener('keypress', function (e) {
      if (e.key === 'Enter') {
        doSearch();
      }
    });
    
    if (!btnAdSearch) {
      btnAdSearch = document.getElementById("btnAdSearch");
    }
    btnAdSearch.addEventListener("click", doSearch);
  });

  async function doSearch() {
    var add = document.getElementById('txtAdSearchString').value.toString().trim();
    if (propertySearch.sanitize) {
      add = propertySearch.sanitize(add);
    }
    view.graphics = null;
    let results = [];

    // Single query scenario with its own error handling
    if (typeof propertySearch.url == 'string') {
      let whereStatement = propertySearch.where(add);
      try {
        let result = await getResults(propertySearch.url, whereStatement);
        if (result && result.features) {
          result.features.forEach(feature => {
            feature.attributes.type = propertySearch.type;
          });
          results = results.concat(result.features);
        }
      } catch (error) {
        console.error("Error during single query:", error);
      }
    }
    // Multiple query scenario with independent error handling for each query
    else if (typeof propertySearch.url == 'object') {
      let resultPromises = [];

      for (const [urlToSearch, config] of Object.entries(propertySearch.url)) {
        let whereStatement = config.where(add);
        if (whereStatement)
        {
          resultPromises.push(
            getResults(urlToSearch, whereStatement)
              .then(result => {
                result.features.forEach(feature => {
                  feature.attributes.type = config.type;
                });
                return result.features;
              })
              .catch(error => {
                console.error(`Error querying ${urlToSearch}:`, error);
                return []; // Return an empty array if an error occurs
              })
          );
        }
      }
      
      let resolvedResults = await Promise.all(resultPromises);
      resolvedResults.forEach(resultArray => {
        results = results.concat(resultArray);
      });
    }

    let outputDiv = document.getElementById('AdSearchResults');
    outputDiv.innerHTML = "";

    if (results.length === 0) {
      let resultDiv = document.createElement('div');
      resultDiv.innerHTML = "No results.";
      outputDiv.appendChild(resultDiv);
      return;
    }

    results.forEach(function(CurrentFeature) {
      let featureAttributes = CurrentFeature.attributes;
      const selectedParcelExtent = CurrentFeature.geometry.extent ?? {
        xmin: CurrentFeature.geometry.x,
        xmax: CurrentFeature.geometry.x,
        ymin: CurrentFeature.geometry.y,
        ymax: CurrentFeature.geometry.y
      };

      let graphic = CurrentFeature;
      graphic.symbol = (CurrentFeature.geometry.type == "point") ? pointSymbol : symbol;
      view.graphics.add(graphic);

      featureAttributes.SelParcelExtentCoords = `${selectedParcelExtent.xmin}, ${selectedParcelExtent.ymin}, ${selectedParcelExtent.xmax}, ${selectedParcelExtent.ymax}`;
      
      let s = propertySearch.output(featureAttributes);
      let resultDiv = document.createElement('div');
      resultDiv.innerHTML = (typeof s == "string") ? s : "";
      if (typeof s !== "string") resultDiv.append(s);

      if (featureAttributes?.OptOut != "Y") {
        if (propertySearch.summary) {
          const parcelLink = document.createElement('calcite-button');
          parcelLink.dataset.pin = featureAttributes.pin;
          parcelLink.classList.add("mt-2");
          setupParcelLink(parcelLink, featureAttributes.PIN ?? featureAttributes.pin, propertySearch.summary);
          parcelLink.textContent = 'View Parcel and Details';
          resultDiv.appendChild(parcelLink);
          resultDiv.appendChild(document.createElement('br'));
        }

        let zoomBtn = document.createElement('calcite-button');
        zoomBtn.innerHTML = "Zoom To";
        zoomBtn.classList = "mt-2 mb-4";
        zoomBtn.addEventListener("click", () => {
          WinGISMap.view.goTo(CurrentFeature.geometry);
          console.log(WinGISMap.view.scale);
          if (propertySearch.zoomScale)
          {
            WinGISMap.view.scale = propertySearch.zoomScale  
          }
          else {
            WinGISMap.view.scale /= 3; 
          }
        });
        resultDiv.appendChild(zoomBtn);
      } else {
        let optoutmsg = document.createElement('span');
        optoutmsg.innerText = "Parcel has opted out of WinGIS services.";
        resultDiv.appendChild(optoutmsg);
      }
      outputDiv.appendChild(resultDiv);
    });
    
    var AOI = results;
    view.goTo(AOI);
  }
}

async function getResults(url, where) {
  try {
    return query.executeQueryJSON(url, {  
      where: where,
      outFields: ["*"],
      returnGeometry: true
    });
  }
  catch (error) {
    console.error(error);
    throw error; // Propagate error to be handled in caller
  }
}
